import React, { useMemo } from 'react';
import { Grid, Stack, Typography, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import FormCard from '../../../components/FormCard';
import {
  getSchemaByBanco,
  getByPath,
  formatValue,
  isEmptyValue
} from '../../../utils/respostaBancoSchema';

// ============================================================================
// Card "DADOS DA RESPOSTA DO BANCO" — exibe a resposta bruta da simulacao
// (descricaoRespostaBanco) num layout chave/valor agrupado, conforme o
// schema declarativo do banco (respostaBancoSchema.js).
//
// Renderiza nada quando:
//   - nao ha data (simulacao antiga / sem integracao)
//   - nao ha schema para o idBanco
//   - nenhum campo do schema tem valor presente
//
// Campos vazios sao omitidos; grupos que ficam sem nenhum campo tambem.
// ============================================================================

const RespostaBancoCard = ({ idBanco, data }) => {
  const theme = useTheme();

  const schema = getSchemaByBanco(idBanco);

  // Resolve cada grupo, mantendo so os campos com valor presente.
  const gruposPreenchidos = useMemo(() => {
    if (!schema || !data || typeof data !== 'object') return [];
    return schema
      .map((grupo) => ({
        group: grupo.group,
        fields: grupo.fields
          .map((field) => ({
            ...field,
            rawValue: getByPath(data, field.path)
          }))
          .filter((field) => !isEmptyValue(field.rawValue))
      }))
      .filter((grupo) => grupo.fields.length > 0);
  }, [schema, data]);

  if (gruposPreenchidos.length === 0) {
    return null;
  }

  return (
    <FormCard title={'DADOS DA RESPOSTA DO BANCO'} border={false}>
      <Stack spacing={2.5}>
        {gruposPreenchidos.map((grupo, idx) => (
          <Stack key={grupo.group} spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: theme.palette.text.secondary,
                fontWeight: 700,
                fontSize: '0.72rem'
              }}
            >
              {grupo.group}
            </Typography>

            <Grid container spacing={1.5}>
              {grupo.fields.map((field) => (
                <Grid item xs={12} sm={6} md={4} key={field.path}>
                  <Stack spacing={0.25}>
                    <Typography variant="caption" color="text.secondary">
                      {field.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatValue(field.rawValue, field.format)}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>

            {idx < gruposPreenchidos.length - 1 && <Divider sx={{ mt: 1 }} />}
          </Stack>
        ))}
      </Stack>
    </FormCard>
  );
};

export default RespostaBancoCard;
